import { useEffect, useState } from "react";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { MagneticButton } from "./MagneticButton";
import { Logo } from "./Logo";
import { CONTACT } from "../lib/data";

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Process", href: "#process" },
  { label: "Pricing", href: "#pricing" },
  { label: "Reviews", href: "#testimonials" },
  { label: "FAQ", href: "#faq" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <motion.div
        className="fixed left-0 top-0 z-[70] h-[2px] origin-left bg-gradient-to-r from-[#FFF27A] to-[#FFD000]"
        style={{ scaleX, width: "100%" }}
      />
      <motion.header
        data-testid="navbar"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed inset-x-0 top-0 z-[60] transition-all duration-500 ${
          scrolled ? "py-3" : "py-5"
        }`}
      >
        <nav className={`mx-auto flex max-w-7xl items-center justify-between px-6 md:px-10 ${
          scrolled ? "glass rounded-full mx-4 md:mx-auto !py-3 px-6" : ""
        }`}>
          <button onClick={() => go("#hero")} data-testid="nav-logo" className="text-xl">
            <Logo className="text-xl" iconClass="h-5 w-5" />
          </button>

          <div className="hidden items-center gap-8 lg:flex">
            {LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-${l.label.toLowerCase()}`}
                onClick={() => go(l.href)}
                className="group relative text-sm text-white/70 transition-colors hover:text-white"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <a href={CONTACT.phoneHref} data-testid="nav-phone" className="hidden items-center gap-2 text-sm text-white/70 transition-colors hover:text-gold md:flex">
              <Phone className="h-4 w-4" strokeWidth={1.5} />
              {CONTACT.phone}
            </a>
            <MagneticButton
              data-testid="nav-book-btn"
              onClick={() => go("#contact")}
              className="hidden rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:bg-gold-hover sm:block"
            >
              Book Now
            </MagneticButton>
            <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} className="lg:hidden text-white">
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-x-4 top-20 z-[65] glass rounded-3xl p-6 lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-4">
              {LINKS.map((l) => (
                <button key={l.href} onClick={() => go(l.href)} className="text-left font-display text-2xl text-white/80">
                  {l.label}
                </button>
              ))}
              <button onClick={() => go("#contact")} className="mt-2 rounded-full bg-gold px-5 py-3 font-semibold text-ink">
                Book Now
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
